import { createContext, useContext, useState, useCallback, useMemo, useEffect, ReactNode } from 'react';
import { UserType } from '../types';

// Permission hierarchy (mirrors backend). 'outreach' is a parallel limited-write
// role: read everywhere + edit valuation fields (cost / currency / acquisition).
const permissionHierarchy: Record<string, string[]> = {
  super_admin: ['super_admin', 'admin', 'classify', 'outreach', 'read'],
  admin: ['admin', 'classify', 'outreach', 'read'],
  classify: ['classify', 'read'],
  outreach: ['outreach', 'read'],
  read: ['read'],
};

interface UserInfo {
  id: string;
  username: string;
  type: UserType;
  avatar_url?: string | null;
  display_name?: string | null;
  email?: string | null;
  show_cost?: boolean;
  show_mind_map?: boolean;
  show_acquisition_method?: boolean;
  show_whois?: boolean;
  show_footprint_audit?: boolean;
  can_trigger_sync?: boolean;
  can_manage_nameservers?: boolean;
  can_renew_domains?: boolean;
}

interface ApiContextValue {
  token: string | null;
  user: UserInfo | null;
  isAuthenticated: boolean;
  hasPermission: (perm: string) => boolean;
  canClassify: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  canSeeCost: boolean;
  canSeeMindMap: boolean;
  canSeeAcquisition: boolean;
  canSeeWhois: boolean;
  canSeeFootprintAudit: boolean;
  /** Can trigger a registrar sync on demand (admin, or the explicit grant). */
  canTriggerSync: boolean;
  /** Can change nameservers at the registrar (admin, or the explicit grant). */
  canManageNameservers: boolean;
  /** Can renew domains (super admin, or the explicit grant). Charges the registrar. */
  canRenewDomains: boolean;
  /** Can edit valuation fields (cost / currency / acquisition method). */
  canEditValuation: boolean;
  login: (token: string, user: UserInfo) => void;
  logout: () => void;
  fetchApi: <T>(path: string, options?: RequestInit) => Promise<T>;
}

const ApiContext = createContext<ApiContextValue | null>(null);

export function ApiProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => {
    // Clean up old auth format
    if (localStorage.getItem('apiKey')) {
      localStorage.removeItem('apiKey');
    }
    return localStorage.getItem('authToken');
  });

  const [user, setUser] = useState<UserInfo | null>(() => {
    const stored = localStorage.getItem('authUser');
    try {
      return stored ? JSON.parse(stored) : null;
    } catch {
      localStorage.removeItem('authUser');
      return null;
    }
  });

  const effectivePermissions = useMemo(() => {
    if (!user) return [];
    return permissionHierarchy[user.type] || ['read'];
  }, [user]);

  const hasPermission = useCallback((perm: string): boolean => {
    return effectivePermissions.includes(perm);
  }, [effectivePermissions]);

  const login = useCallback((newToken: string, userInfo: UserInfo) => {
    setToken(newToken);
    setUser(userInfo);
    localStorage.setItem('authToken', newToken);
    localStorage.setItem('authUser', JSON.stringify(userInfo));
  }, []);

  const logout = useCallback(() => {
    const currentToken = localStorage.getItem('authToken');
    if (currentToken) {
      const apiBaseUrl = import.meta.env.VITE_API_URL || '';
      fetch(`${apiBaseUrl}/api/auth/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${currentToken}` },
      }).catch(() => {});
    }
    setToken(null);
    setUser(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
  }, []);

  // Refresh the stored user from /me on mount (and whenever the token changes).
  // The user object is otherwise frozen at login time, so admin changes to
  // visibility flags (cost / mind map / acquisition) or role would not take
  // effect until the user logged out and back in. /me returns the live flags.
  useEffect(() => {
    if (!token) return;
    const apiBaseUrl = import.meta.env.VITE_API_URL || '';
    fetch(`${apiBaseUrl}/api/auth/me`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        if (body?.success && body.data) {
          setUser(body.data);
          localStorage.setItem('authUser', JSON.stringify(body.data));
        }
      })
      .catch(() => { /* offline / transient — keep cached user */ });
  }, [token]);

  const fetchApi = useCallback(async <T,>(path: string, options: RequestInit = {}): Promise<T> => {
    if (!token) {
      throw new Error('Not authenticated');
    }

    const apiBaseUrl = import.meta.env.VITE_API_URL || '';
    const response = await fetch(`${apiBaseUrl}/api${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });

    // Never assume the body is JSON. A proxy or gateway between us and the API
    // can answer with its own HTML error page, and parsing that unconditionally
    // surfaced "Unexpected token '<'" instead of the actual failure.
    const raw = await response.text();
    let data: unknown;
    try {
      data = raw ? JSON.parse(raw) : {};
    } catch {
      data = null;
    }

    if (!response.ok) {
      if (response.status === 401) {
        setToken(null);
        setUser(null);
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
      }
      const apiError = (data as { error?: string } | null)?.error;
      throw new Error(apiError || `Request failed (HTTP ${response.status} ${response.statusText})`.trim());
    }

    if (data === null) {
      throw new Error(
        `The server returned an unexpected non-JSON response (HTTP ${response.status}). The request may have been interrupted by a gateway or proxy — the underlying operation may still have completed.`
      );
    }

    return data as T;
  }, [token]);

  return (
    <ApiContext.Provider
      value={{
        token,
        user,
        isAuthenticated: !!token,
        hasPermission,
        canClassify: hasPermission('classify'),
        isAdmin: hasPermission('admin'),
        isSuperAdmin: hasPermission('super_admin'),
        canSeeCost: hasPermission('super_admin') || hasPermission('outreach') || !!(user?.show_cost),
        canSeeMindMap: hasPermission('super_admin') || !!(user?.show_mind_map),
        canSeeAcquisition: hasPermission('super_admin') || hasPermission('outreach') || !!(user?.show_acquisition_method),
        canSeeWhois: hasPermission('admin') || !!(user?.show_whois),
        canSeeFootprintAudit: hasPermission('admin') || !!(user?.show_footprint_audit),
        canTriggerSync: hasPermission('admin') || !!(user?.can_trigger_sync),
        canManageNameservers: hasPermission('admin') || !!(user?.can_manage_nameservers),
        canRenewDomains: hasPermission('super_admin') || !!(user?.can_renew_domains),
        canEditValuation: hasPermission('admin') || hasPermission('outreach'),
        login,
        logout,
        fetchApi,
      }}
    >
      {children}
    </ApiContext.Provider>
  );
}

export function useApi() {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error('useApi must be used within ApiProvider');
  }
  return context;
}
